import { useNavigate } from 'react-router-dom';
import { Business, Person } from '@mui/icons-material';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useClearRecentEntities,
  useRecentEntities,
} from '../hooks/useRecentEntities';
import { entityHomePath } from '../utils/recent-entities';

/**
 * Recently visited organizations and users (GDN-2777).
 *
 * Admins work a handful of customers at a time, so "where was I" is worth more
 * sidebar space than a browse list of every org — the header search already
 * handles finding one by name. Renders nothing until there's a trail, so a
 * fresh session doesn't show an empty heading.
 */
export function RecentEntitiesNav() {
  const navigate = useNavigate();
  const recent = useRecentEntities();
  const clear = useClearRecentEntities();

  if (recent.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          Recent
        </Typography>
        <Tooltip title="Clear recent">
          <Typography
            component="button"
            variant="caption"
            onClick={clear}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: 'text.disabled',
              p: 0,
              '&:hover': { color: 'text.secondary' },
            }}
          >
            Clear
          </Typography>
        </Tooltip>
      </Box>

      <List component="nav" disablePadding>
        {recent.map(entity => {
          const Icon = entity.kind === 'org' ? Business : Person;
          const to = entityHomePath(entity);
          return (
            <ListItemButton
              key={`${entity.kind}:${entity.id}`}
              onClick={() => navigate(to)}
              sx={{ mx: 1, borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={entity.name}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider sx={{ my: 1 }} />
    </>
  );
}
